import React, { useState, type FC } from 'react'

import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue } from 'recoil'

import { FormElement, Select } from 'mailup-web-components'

import { INPUT_MESSAGES_MAX_ITEMS } from 'Constants'

import useAsyncSelect from 'Hooks/useAsyncSelect/useAsyncSelect'

import { modalBulkAddToGroupState } from 'Pages/RecipientsList/recoil/state/modals'

import {
  modalBulkAddToGroupAddGroupCallback,
  modalBulkAddToGroupChangeCallback,
} from 'Pages/RecipientsList/recoil/callbacks/modalBulkAddToGroup'

import {
  fetchGroupById,
  fetchGroups,
} from 'Pages/RecipientsList/recoil/services/asyncSelectGroups'

const InputAddToGroup: FC = () => {
  const intl = useIntl()

  const [forceCollapse, setForceCollapse] = useState(false)

  const { addToGroupId, isAddToGroupIdDirty } = useRecoilValue(
    modalBulkAddToGroupState,
  )

  const addGroup = useRecoilCallback(modalBulkAddToGroupAddGroupCallback, [])
  const modalStateChange = useRecoilCallback(
    modalBulkAddToGroupChangeCallback,
    [],
  )

  const { options, loading, handleSearch, hint, hintNoResults } =
    useAsyncSelect({
      key: 'modal-add-group',
      value: addToGroupId,
      maxItems: INPUT_MESSAGES_MAX_ITEMS,
      fetchItems: async () => await fetchGroups(''),
      fetchItemsByTerm: fetchGroups,
      fetchItemById: fetchGroupById,
      labels: {
        loading: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusLoading',
        }),
        noResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusNoMatches',
        }),
        moreResults: intl.formatMessage({
          id: 'Load.MailupCommon.InputStatusTooManyItems',
        }),
      },
    })

  const handleSelectGroupChange = (v: number): void => {
    void modalStateChange({
      addToGroupId: v,
      isAddToGroupIdDirty: true,
    })
  }

  const handleSelectGroupExpandCollapse = (c: boolean): void => {
    if (!c) {
      handleSearch('')
    }
  }

  const handleSelectGroupSubmit = async (s: string): Promise<void> => {
    await addGroup(s)

    setForceCollapse(true)

    setTimeout(() => {
      setForceCollapse(false)
    }, 250)
  }

  const showSelectGroupFeedback =
    (addToGroupId === undefined || addToGroupId == null) && isAddToGroupIdDirty

  return (
    <FormElement
      label={intl.formatMessage({ id: 'Load.RecipientsList.SelectGroup' })}
      hint={intl.formatMessage(
        {
          id: 'Load.RecipientsList.SelectGroupAddGroupHint',
        },
        {
          b: (chunks) => <strong>{chunks}</strong>,
        },
      )}
      variant={showSelectGroupFeedback ? 'danger' : undefined}
      feedback={
        showSelectGroupFeedback
          ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
          : undefined
      }
    >
      <Select
        value={addToGroupId}
        options={options}
        loading={loading}
        onChange={handleSelectGroupChange}
        onSearch={handleSearch}
        onSubmit={handleSelectGroupSubmit}
        onExpandCollapse={handleSelectGroupExpandCollapse}
        hint={hint}
        hintNoResults={hintNoResults}
        memoSelectOption
        error={showSelectGroupFeedback}
        placeholder={intl.formatMessage({
          id: 'Load.RecipientsList.SelectOrAddGroupPlaceholder',
        })}
        data-mu-test="input-group"
        forceCollapse={forceCollapse}
      />
    </FormElement>
  )
}

export default InputAddToGroup
